/**
 * FieldEditorDialog.tsx - Smart Field Editor with Translatr
 * 
 * CHANGE HISTORY:
 * - 251126_1808 [pending] danielbeer: Smart field editor with Translatr parsing
 * 
 * Features:
 * - Translatr auto-detection and parsing
 * - Extract field names from structured/delimited data
 * - Promptr modal with Esc/Enter support
 * - Format indicator at bottom
 * - Handles JSON, CSV, TSV, XML
 */

import React, { useState, useCallback, useEffect } from 'react';
import type { Field, FieldType } from '../types/index';

// Optional dependencies
let Translatr: any = null;
let Modal: any = null;

try {
  // @ts-ignore
  const dependr = require('@opcentral/dependr');
  Translatr = dependr.Translatr;
  Modal = dependr.Modal;
} catch (e) {
  console.warn('[FieldEditorDialog] Translatr/Modal not available');
}

export interface FieldEditorDialogProps {
  fields: Field[];
  onSave: (fields: Field[]) => void;
  onClose: () => void;
  title?: string;
  placeholder?: string;
}

/**
 * Infer field type from value
 */
function inferFieldType(value: any): FieldType {
  if (value === null || value === undefined) return 'string';
  if (Array.isArray(value)) return 'array';
  if (typeof value === 'object') return 'object';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'boolean';
  
  const str = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return 'date';
  if (/^\d{4}-\d{2}-\d{2}T/.test(str)) return 'datetime';
  if (/^[\w.-]+@[\w.-]+\.\w+$/.test(str)) return 'email';
  if (/^[\d\s()+-]+$/.test(str) && str.length >= 10) return 'phone';
  if (/^https?:\/\//.test(str)) return 'url';
  
  return 'string';
}

/**
 * Smart field editor dialog
 */
export const FieldEditorDialog: React.FC<FieldEditorDialogProps> = ({
  fields,
  onSave,
  onClose,
  title = 'Edit Fields',
  placeholder = 'Paste data or enter field names (one per line)...'
}) => {
  const [inputText, setInputText] = useState(() => 
    fields.map(f => f.name).join('\n')
  );
  const [detectedFormat, setDetectedFormat] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string>('');

  /**
   * Parse input and detect format
   */
  const parseInput = useCallback(async (text: string) => {
    if (!text.trim()) {
      setDetectedFormat('');
      setError('');
      return;
    }

    setIsProcessing(true);
    setError('');

    try {
      // Try Translatr auto-parse
      if (Translatr) {
        const result = await Translatr.autoParse(text);
        
        if (result && result.data) {
          setDetectedFormat(result.format?.toUpperCase() || 'DETECTED');
          setIsProcessing(false);
          return;
        }
      }
      
      // Fallback: treat as plain list
      setDetectedFormat('LIST');
    } catch (err) {
      // If parsing fails, treat as plain list
      setDetectedFormat('LIST');
    }
    
    setIsProcessing(false);
  }, []);

  /**
   * Debounced parse on input change
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      parseInput(inputText);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [inputText, parseInput]);

  /**
   * Extract fields from input
   */
  const extractFields = useCallback(async (text: string): Promise<Field[]> => {
    if (!text.trim()) return [];

    try {
      // Try Translatr parsing
      if (Translatr) {
        const result = await Translatr.autoParse(text);
        
        if (result && result.data) {
          let fieldNames: string[] = [];
          
          // Extract field names based on format
          if (result.format === 'json') {
            if (Array.isArray(result.data) && result.data.length > 0) {
              // Use first record for field names
              fieldNames = Object.keys(result.data[0]);
            } else if (typeof result.data === 'object') {
              // Single object
              fieldNames = Object.keys(result.data);
            }
          } else if (result.format === 'csv' || result.format === 'tsv') {
            if (Array.isArray(result.data) && result.data.length > 0) {
              // Use first record for field names
              fieldNames = Object.keys(result.data[0]);
            }
          }
          
          // Create Field objects with inferred types
          if (fieldNames.length > 0) {
            const firstRecord = Array.isArray(result.data) ? result.data[0] : result.data;
            return fieldNames.map((name, index) => {
              const value = firstRecord[name];
              return {
                id: `field_${Date.now()}_${index}`,
                name,
                type: inferFieldType(value),
                required: false,
                sample: value
              };
            });
          }
        }
      }
    } catch (err) {
      console.warn('[FieldEditorDialog] Parse error:', err);
    }
    
    // Fallback: treat as plain list of field names
    const lines = text.split('\n').filter(line => line.trim());
    return lines.map((line, index) => ({
      id: `field_${Date.now()}_${index}`,
      name: line.trim(),
      type: 'string' as FieldType,
      required: false
    }));
  }, []);

  /**
   * Handle save
   */
  const handleSave = useCallback(async () => {
    try {
      const newFields = await extractFields(inputText);
      onSave(newFields);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to parse fields');
    }
  }, [inputText, extractFields, onSave, onClose]);

  /**
   * Handle keyboard shortcuts
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSave();
    }
  }, [onClose, handleSave]);

  // Use Promptr Modal if available, otherwise fallback to custom dialog
  const isUsingPromptr = !!Modal;

  const dialogContent = (
    <>
      <div className="field-editor-dialog__body">
        <p className="field-editor-dialog__help">
          Paste structured data (JSON, CSV, TSV) or enter field names (one per line)
        </p>
        
        <textarea
          className="field-editor-dialog__textarea"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={12}
          autoFocus
        />
        
        {error && (
          <div className="field-editor-dialog__error">
            ⚠️ {error}
          </div>
        )}
      </div>
      
      <div className="field-editor-dialog__footer">
        <div className="field-editor-dialog__indicator">
          {isProcessing ? (
            <span className="field-editor-dialog__processing">Detecting...</span>
          ) : detectedFormat ? (
            <span className="field-editor-dialog__format">
              📋 Format: <strong>{detectedFormat}</strong>
            </span>
          ) : (
            <span className="field-editor-dialog__hint">
              Paste data to auto-detect format
            </span>
          )}
        </div>
        
        <div className="field-editor-dialog__actions">
          <button onClick={onClose} className="field-editor-dialog__btn">
            Cancel <span className="field-editor-dialog__shortcut">Esc</span>
          </button>
          <button onClick={handleSave} className="field-editor-dialog__btn field-editor-dialog__btn--primary">
            Save <span className="field-editor-dialog__shortcut">⌘↵</span>
          </button>
        </div>
      </div>
    </>
  );

  if (isUsingPromptr) {
    return (
      <Modal
        isOpen={true}
        onClose={onClose}
        title={title}
        size="large"
      >
        {dialogContent}
      </Modal>
    );
  }

  // Fallback to custom dialog
  return (
    <div className="field-editor-dialog">
      <div className="field-editor-dialog__overlay" onClick={onClose} />
      <div className="field-editor-dialog__content">
        <div className="field-editor-dialog__header">
          <h3>{title}</h3>
          <button onClick={onClose} className="field-editor-dialog__close">×</button>
        </div>
        {dialogContent}
      </div>
    </div>
  );
};
